<template>
  <div class="registry-table-wrapper">
    <modal-box
      :is-active="isModalActive"
      :trash-object-name="trashObjectName"
      @confirm="trashConfirm"
      @cancel="trashCancel"
    />

    <!-- Search Panel -->
    <div class="search-panel">
      <div class="search-panel-header" @click="searchPanelOpen = !searchPanelOpen">
        <span class="search-panel-title">
          <span class="search-icon-bubble" :class="{ 'has-active': activeSearchCount > 0 }">
            <b-icon icon="magnify" size="is-small" />
          </span>
          Cautare
          <span v-if="activeSearchCount > 0" class="tag is-info is-light ml-2">
            {{ activeSearchCount }} activ{{ activeSearchCount > 1 ? 'e' : '' }}
          </span>
        </span>
        <b-icon :icon="searchPanelOpen ? 'chevron-up' : 'chevron-down'" size="is-small" />
      </div>

      <transition name="panel-slide">
        <div v-if="searchPanelOpen" class="search-panel-body">
          <div class="search-grid">
            <b-field label="Nr. Inreg. intern">
              <b-input v-model="searchTerms.internalRegistrationNumber" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Continut">
              <b-input v-model="searchTerms.description" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Emitent">
              <b-input v-model="searchTerms.issuer" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Departament">
              <b-input v-model="searchTerms.department" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Nr. Inreg. extern">
              <b-input v-model="searchTerms.externalRegistrationNumber" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
            <b-field label="Continut raspuns">
              <b-input v-model="searchTerms.responseDescription" @input="onSearchInput" placeholder="Cauta..." icon="magnify" size="is-small" clearable />
            </b-field>
          </div>
          <div class="search-panel-actions">
            <button class="button is-small is-light" @click="clearSearch" :disabled="activeSearchCount === 0">
              <b-icon icon="close" size="is-small" />
              <span>Reseteaza</span>
            </button>
          </div>
        </div>
      </transition>
    </div>

    <!-- Table -->
    <b-table
      :loading="isLoading"
      :paginated="true"
      :backend-pagination="true"
      :total="total"
      :per-page="perPage"
      :current-page="currentPage"
      :striped="true"
      :hoverable="true"
      default-sort="name"
      :data="entries"
      @page-change="$emit('page-change', $event)"
    >
      <!-- Nr. Inreg. intern -->
      <b-table-column field="internalRegistrationNumber" label="Nr. Inreg." sortable centered width="110" v-slot="props">
        <span class="has-text-weight-semibold">{{ props.row.internalRegistrationNumber }}</span>
      </b-table-column>

      <!-- Data -->
      <b-table-column field="internalRegistrationDate" label="Data" sortable centered width="110" v-slot="props">
        {{ props.row.internalRegistrationDate | formatDate }}
      </b-table-column>

      <!-- Continut -->
      <b-table-column field="description" label="Continut" sortable v-slot="props">
        <div class="description-cell" :title="props.row.description">{{ props.row.description }}</div>
      </b-table-column>

      <!-- Emitent -->
      <b-table-column field="issuer" label="Emitent" sortable centered v-slot="props">
        {{ props.row.issuer }}
      </b-table-column>

      <!-- Status -->
      <b-table-column field="status" label="Status" sortable centered width="130" v-slot="props">
        <span :class="getStatusClass(props.row)">
          <b-icon :icon="getStatusIcon(props.row)" size="is-small" />
          {{ props.row.status }}
        </span>
      </b-table-column>

      <!-- Actions -->
      <b-table-column centered width="120" v-slot="props">
        <div class="row-actions">
          <b-tooltip label="Vizualizeaza" position="is-top" type="is-dark" :delay="300">
            <button class="button is-small is-light preview-btn" @click.stop="openPreview(props.row)">
              <b-icon icon="eye" size="is-small" />
            </button>
          </b-tooltip>
          <router-link
            :to="{ name: 'registryEntry.edit', params: { registryId: props.row.registryId, id: props.row.id } }"
            class="button is-small is-primary"
            title="Editare"
            @click.native.stop
          >
            <b-icon icon="file-edit" size="is-small" />
          </router-link>
          <button
            v-if="canDelete(props.row.internalRegistrationNumber)"
            class="button is-small is-danger"
            type="button"
            @click.stop="trashModal(props.row)"
          >
            <b-icon icon="trash-can" size="is-small" />
          </button>
        </div>
      </b-table-column>

      <section class="section" slot="empty">
        <div class="content has-text-grey has-text-centered">
          <template v-if="isLoading">
            <p><b-icon icon="dots-horizontal" size="is-large" /></p>
            <p>Incarcare data...</p>
          </template>
          <template v-else>
            <p><b-icon icon="emoticon-sad" size="is-large" /></p>
            <p>Nici o inregistrare&hellip;</p>
          </template>
        </div>
      </section>
    </b-table>

    <!-- Preview Modal -->
    <b-modal :active.sync="previewOpen" :width="580" scroll="keep" :can-cancel="['escape', 'outside']">
      <div class="preview-card" v-if="previewEntry">
        <div class="preview-header">
          <div class="preview-header-left">
            <span class="preview-nr">Nr. {{ previewEntry.internalRegistrationNumber }}</span>
            <span class="preview-date">{{ previewEntry.internalRegistrationDate | formatDate }}</span>
          </div>
          <span :class="getStatusClass(previewEntry)">
            <b-icon :icon="getStatusIcon(previewEntry)" size="is-small" />
            {{ previewEntry.status }}
          </span>
        </div>

        <div class="preview-body">
          <div class="preview-field preview-field--full">
            <span class="preview-label">Continut</span>
            <span class="preview-value">{{ previewEntry.description || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Emitent</span>
            <span class="preview-value">{{ previewEntry.issuer || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Departament</span>
            <span class="preview-value">{{ previewEntry.department || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Nr. Inreg. extern</span>
            <span class="preview-value">{{ previewEntry.externalRegistrationNumber || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Tip document</span>
            <span class="preview-value">{{ previewEntry.type || '—' }}</span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Data limita raspuns</span>
            <span
              class="preview-value"
              :class="isOverdue(previewEntry) ? 'has-text-danger has-text-weight-semibold' : ''"
            >
              {{ previewEntry.deadline ? $options.filters.formatDate(previewEntry.deadline) : '—' }}
              <span v-if="isOverdue(previewEntry)" class="tag is-danger is-light ml-1">Depasit</span>
            </span>
          </div>

          <div class="preview-field">
            <span class="preview-label">Data raspuns</span>
            <span class="preview-value">
              {{ previewEntry.responseDate ? $options.filters.formatDate(previewEntry.responseDate) : '—' }}
            </span>
          </div>

          <div class="preview-field preview-field--full" v-if="previewEntry.responseDescription">
            <span class="preview-label">Continut raspuns</span>
            <span class="preview-value">{{ previewEntry.responseDescription }}</span>
          </div>
        </div>

        <div class="preview-footer">
          <router-link
            :to="{ name: 'registryEntry.edit', params: { registryId: previewEntry.registryId, id: previewEntry.id } }"
            class="button is-primary"
          >
            <b-icon icon="file-edit" size="is-small" />
            <span>Editare</span>
          </router-link>
          <button class="button is-light" @click="previewOpen = false">Inchide</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ModalBox from "@/components/ModalBox";
import { isNilOrEmpty, openBlankPage } from "@/helpers/utils";
import moment from "moment";
import { accessMixin } from "../../mixins/accessMixin";
import debounce from "lodash/debounce";

const SEARCH_FIELDS = [
  "internalRegistrationNumber",
  "description",
  "issuer",
  "department",
  "externalRegistrationNumber",
  "responseDescription",
];

export default {
  name: "RegistryEntriesTable",
  components: { ModalBox },
  mixins: [accessMixin],
  props: {
    entries: { type: Array, default: null },
    maxEntry: { type: Number, default: 0 },
    isLoading: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 },
  },
  data() {
    return {
      isModalActive: false,
      trashObject: null,
      perPage: 100,
      searchTerms: Object.fromEntries(SEARCH_FIELDS.map((f) => [f, ""])),
      searchPanelOpen: false,
      previewOpen: false,
      previewEntry: null,
    };
  },
  computed: {
    activeSearchCount() {
      return Object.values(this.searchTerms).filter((v) => v && v.trim() !== "").length;
    },
    trashObjectName() {
      if (this.trashObject) {
        return (
          this.trashObject.internalRegistrationNumber +
          " din data " +
          moment(this.trashObject.internalRegistrationDate).format("DD/MM/YYYY")
        );
      }
      return null;
    },
  },
  created() {
    this.emitSearch = debounce(() => {
      this.$emit("search", { ...this.searchTerms });
    }, 300);
  },
  methods: {
    onSearchInput() {
      this.emitSearch();
    },
    clearSearch() {
      SEARCH_FIELDS.forEach((f) => this.$set(this.searchTerms, f, ""));
      this.$emit("search", { ...this.searchTerms });
    },
    openPreview(row) {
      this.previewEntry = row;
      this.previewOpen = true;
    },
    isOverdue(entry) {
      if (!entry.deadline || entry.status === "Rezolvat") return false;
      return moment(entry.deadline).isBefore(moment(), "day");
    },
    canDelete(currentId) {
      return (
        (currentId === this.maxEntry && this.hasRegistraturaAccess()) ||
        (currentId === this.maxEntry && this.hasAdminToRegistry())
      );
    },
    getStatusClass(registryEntry) {
      if (registryEntry.status === "Rezolvat") return "tag is-success";
      if (registryEntry.status === "În lucru") return "tag is-warning";
      return "tag";
    },
    getStatusIcon(registryEntry) {
      if (registryEntry.status === "Rezolvat") return "check-circle";
      if (registryEntry.status === "În lucru") return "clock-outline";
      return "";
    },
    trashModal(trashObject) {
      this.trashObject = trashObject;
      this.isModalActive = true;
    },
    downloadFile(file) {
      if (!isNilOrEmpty(file)) {
        openBlankPage(file);
      }
    },
    trashConfirm() {
      this.isModalActive = false;
      this.$emit("handleDeleteRegistryEntry", this.trashObject.id);
    },
    trashCancel() {
      this.isModalActive = false;
    },
  },
};
</script>

<style scoped>
/* Search Panel */
.search-panel {
  border: 1px solid #dbdbdb;
  border-radius: 6px;
  margin-bottom: 1rem;
  background: #fff;
  overflow: hidden;
}

.search-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  cursor: pointer;
  user-select: none;
  background: #fafafa;
  transition: background 0.15s;
}

.search-panel-header:hover {
  background: #f0f0f0;
}

.search-panel-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.search-icon-bubble {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #3273dc;
  color: #fff;
  flex-shrink: 0;
  transition: background-color 0.2s;
}

.search-icon-bubble.has-active {
  background-color: #2160c4;
  box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.25);
}

.search-panel-body {
  padding: 1rem 1rem 0.75rem;
  border-top: 1px solid #f0f0f0;
}

.search-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem 1rem;
}

.search-grid :deep(.label) {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.search-panel-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.panel-slide-enter,
.panel-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Table */
.description-cell {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-actions {
  display: flex;
  gap: 0.35rem;
  justify-content: center;
}

.preview-btn:hover {
  background-color: #e8f0fe;
  color: #3273dc;
  border-color: #3273dc;
}

/* Preview Modal */
.preview-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #ededed;
}

.preview-header-left {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.preview-nr {
  font-size: 1.15rem;
  font-weight: 700;
  color: #363636;
}

.preview-date {
  font-size: 0.8rem;
  color: #7a7a7a;
}

.preview-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  padding: 1.25rem 1.5rem;
}

.preview-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.preview-field--full {
  grid-column: 1 / -1;
}

.preview-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9a9a9a;
}

.preview-value {
  font-size: 0.92rem;
  color: #363636;
  line-height: 1.45;
}

.preview-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #ededed;
  background: #fafafa;
}
</style>
